import React, { useMemo, useState } from "react";
import { Box, Button, Flex, Icon, Text, Textarea } from "@chakra-ui/react";
import { HiMinus, HiPlus, HiOutlinePencil, HiCheck, HiOutlineTv } from "react-icons/hi2";
import { c } from "../../classroomColors";
import { STANDE } from "../../standeTokens";
import { KIND_LABEL } from "./useLiveActivity";
import { Chip, Countdown, Eyebrow, Initial, MarkPill, blueButton, inkButton, quietButton } from "./parts";

/** One student's answer, as the teacher reviews it: the AI's mark and words,
 *  which the teacher can change before anything goes out. */
const AnswerCard = ({ r, total, onReview, onRelease }) => {
  const [editing, setEditing] = useState(false);
  const [words, setWords] = useState("");
  const mark = r.final_mark ?? r.ai_mark;
  const feedback = r.final_feedback || r.ai_feedback;
  const pending = r.marking_status === "MARKING";
  const failed = r.marking_status === "FAILED" && r.final_mark == null;

  const setMark = (m) => onReview(r.guid, { final_mark: Math.max(0, Math.min(total, m)) });

  return (
    <Box p="12px" borderRadius="14px" bg={c.surface} border="1px solid" borderColor={r.needs_review ? STANDE.darkYellow : c.borderSoft}>
      <Flex align="center" gap="10px" mb="8px">
        <Initial name={r.student_name} />
        <Text fontSize="13px" fontWeight="600" color={c.text} flex="1" noOfLines={1}>
          {r.student_name}
        </Text>
        {r.needs_review && !r.released && <Chip tone="yellow">Needs your eyes</Chip>}
        {r.released && (
          <Chip tone="green">
            <Icon as={HiCheck} boxSize="11px" /> Sent
          </Chip>
        )}
        {pending ? (
          <Chip>Marking…</Chip>
        ) : (
          <Flex align="center" gap="2px">
            <MarkButton label="One mark less" icon={HiMinus} onClick={() => setMark((mark ?? 0) - 1)} disabled={(mark ?? 0) <= 0} />
            {mark == null ? <Chip tone="red">–/{total}</Chip> : <MarkPill mark={mark} total={total} />}
            <MarkButton label="One mark more" icon={HiPlus} onClick={() => setMark((mark ?? -1) + 1)} disabled={(mark ?? 0) >= total} />
          </Flex>
        )}
      </Flex>

      <Box px="10px" py="7px" borderRadius="10px" bg={c.surfaceSubtle} fontSize="13px" color={c.text} whiteSpace="pre-wrap">
        {r.answer_text}
      </Box>

      {failed && (
        <Text fontSize="12px" color={c.textSub} mt="8px">
          The marker could not mark this one. Give it a mark and it can go out.
        </Text>
      )}

      {!pending && !failed && (
        <Box mt="8px" px="10px" py="8px" borderRadius="10px" bg={c.infoSoft}>
          <Flex align="center" justify="space-between" mb="2px">
            <Text fontSize="10.5px" letterSpacing="0.1em" fontWeight="600" color={c.infoText}>
              {r.final_feedback ? "YOUR FEEDBACK" : "AI FEEDBACK"}
            </Text>
            {!editing && !r.released && (
              <Flex as="button" type="button" align="center" gap="4px" fontSize="11px" color={c.infoText}
                onClick={() => { setWords(feedback || ""); setEditing(true); }}>
                <Icon as={HiOutlinePencil} boxSize="11px" /> Edit
              </Flex>
            )}
          </Flex>
          {editing ? (
            <>
              <Textarea value={words} onChange={(e) => setWords(e.target.value)} size="sm" fontSize="12.5px" bg={c.surface}
                borderColor={c.borderSoft} borderRadius="8px" rows={3} mt="4px" autoFocus />
              <Flex gap="6px" mt="6px" justify="flex-end">
                <Button size="xs" {...quietButton} borderRadius="8px" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="xs" {...inkButton} borderRadius="8px"
                  onClick={async () => { await onReview(r.guid, { final_feedback: words }); setEditing(false); }}>
                  Save
                </Button>
              </Flex>
            </>
          ) : (
            <Text fontSize="12.5px" color={c.text} lineHeight="1.45" whiteSpace="pre-wrap" noOfLines={5}>
              {feedback}
            </Text>
          )}
        </Box>
      )}

      {!r.released && !pending && (
        <Flex justify="flex-end" mt="8px">
          <Button size="sm" h="30px" {...inkButton} borderRadius="9px" fontSize="12px" fontWeight="500"
            isDisabled={failed} onClick={() => onRelease([r.guid])}>
            Approve & send
          </Button>
        </Flex>
      )}
    </Box>
  );
};

const MarkButton = ({ label, icon, onClick, disabled }) => (
  <Flex as="button" type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
    boxSize="22px" borderRadius="full" align="center" justify="center" color={c.textSub}
    opacity={disabled ? 0.35 : 1} _hover={disabled ? {} : { bg: c.hover, color: c.text }}>
    <Icon as={icon} boxSize="11px" />
  </Flex>
);

/**
 * The activity while it runs, for the teacher (in the dock).
 *
 * Top: what is happening — which question, the clock, how many have
 * answered — and the one next step (next question, or end). Below: the
 * answers as they are marked, with the ones worth a look first-class
 * ("Needs your eyes"), each approved and sent on its own or all at once.
 * The big screen is one press away ("Show the class"), and so is the class
 * picture once answers are in.
 */
const LiveActivityPanel = ({ live, presenting, onPresent, onShowResults, onNew }) => {
  const { activity, act, clock, error } = live;
  const [filter, setFilter] = useState("all");
  const [questionGuid, setQuestionGuid] = useState(null);

  const byQuestion = useMemo(() => {
    const map = {};
    (activity?.questions || []).forEach((q) => { map[q.guid] = q; });
    return map;
  }, [activity]);

  if (!activity) return null;
  const isRace = activity.kind === "QUIZ_RACE";
  const isEscape = activity.kind === "ESCAPE_ROOM";
  const isLive = activity.status === "LIVE";
  const current = activity.questions[activity.current_index];
  const shownQuestion = questionGuid || (isRace ? current?.guid : null);

  const responses = activity.responses.filter((r) => !shownQuestion || r.question_guid === shownQuestion);
  const needsEyes = responses.filter((r) => r.needs_review && !r.released);
  const list = filter === "eyes" ? needsEyes : responses;
  const releasable = activity.responses.filter((r) => !r.released && (r.marking_status === "MARKED" || r.final_mark != null));
  const answeredNow = isRace ? activity.answered_counts[current?.guid] || 0 : new Set(activity.responses.map((r) => r.user_guid)).size;

  const review = (guid, body) => act((api, s, id) => api.review(s, id, guid, body));
  const release = (guids) => act((api, s, id) => api.release(s, id, guids));

  return (
    <Flex direction="column" minH={0} flex="1 1 auto">
      <Box px="16px" pb="12px">
        <Flex align="center" gap="8px" mb="10px" wrap="wrap">
          <Chip tone="ink">{KIND_LABEL[activity.kind]}</Chip>
          {isLive ? (
            <Chip tone="green">
              <Box boxSize="6px" borderRadius="full" bg={STANDE.darkGreen} /> Live
            </Chip>
          ) : (
            <Chip>{isEscape && activity.escaped ? "Escaped" : "Ended"}</Chip>
          )}
          {isRace && <Chip>Question {activity.current_index + 1} of {activity.questions.length}</Chip>}
          <Box flex="1" />
          {isRace && isLive && <Countdown activity={activity} clock={clock} />}
        </Flex>

        <Flex align="baseline" gap="6px">
          <Text fontSize="26px" fontWeight="600" color={c.text} sx={{ fontVariantNumeric: "tabular-nums" }}>
            {answeredNow}/{activity.class_size}
          </Text>
          <Text fontSize="12px" color={c.textSub}>{isRace ? "answered this question" : "have answered"}</Text>
        </Flex>

        {isRace && current && (
          <Text fontSize="13px" color={c.text} mt="6px" noOfLines={3}>{current.question_text}</Text>
        )}
        {isEscape && (
          <Flex gap="6px" mt="8px" wrap="wrap">
            {activity.locks.map((l) => (
              <Chip key={l.question_guid} tone={l.cracked ? "green" : "grey"}>
                {l.cracked ? `${l.name} · ${l.digit}` : l.name}
              </Chip>
            ))}
          </Flex>
        )}

        <Flex gap="8px" mt="12px">
          {isLive && isRace && activity.current_index < activity.questions.length - 1 && (
            <Button {...inkButton} size="sm" h="34px" flex="1" borderRadius="10px" fontSize="12.5px" fontWeight="500"
              onClick={() => { setQuestionGuid(null); act((api, s, id) => api.advance(s, id)); }}>
              Next question
            </Button>
          )}
          {isLive && (
            <Button {...(isRace && activity.current_index < activity.questions.length - 1 ? quietButton : inkButton)}
              size="sm" h="34px" flex="1" borderRadius="10px" fontSize="12.5px" fontWeight="500"
              onClick={() => act((api, s, id) => api.end(s, id))}>
              End activity
            </Button>
          )}
          {!isLive && (
            <Button {...inkButton} size="sm" h="34px" flex="1" borderRadius="10px" fontSize="12.5px" fontWeight="500" onClick={onShowResults}>
              Class picture
            </Button>
          )}
          <Button {...quietButton} size="sm" h="34px" borderRadius="10px" fontSize="12.5px" fontWeight="500"
            leftIcon={<Icon as={HiOutlineTv} boxSize="14px" />} onClick={onPresent} aria-pressed={presenting}>
            {presenting ? "Hide" : "Show the class"}
          </Button>
        </Flex>
        {/* The next one. Once this has ended it is the obvious next step;
            while it runs, launching another ends this one, so it is quieter. */}
        {onNew && (
          <Button
            {...(isLive ? quietButton : blueButton)}
            size="sm"
            h="34px"
            w="100%"
            mt="8px"
            borderRadius="10px"
            fontSize="12.5px"
            fontWeight={isLive ? "500" : "600"}
            onClick={onNew}
          >
            {isLive ? "Set up another activity" : "New activity"}
          </Button>
        )}
        {error && <Text fontSize="12px" color={c.accentSoftText} mt="8px">{error}</Text>}
      </Box>

      <Box h="1px" bg={c.borderSoft} mx="16px" />

      <Flex direction="column" px="16px" pt="12px" pb="8px" gap="8px">
        <Flex align="center" justify="space-between">
          <Eyebrow>Marking</Eyebrow>
          <Button size="xs" h="26px" {...inkButton} borderRadius="8px" fontSize="11.5px" fontWeight="500"
            isDisabled={!releasable.length} onClick={() => release(null)}>
            Send all marked{releasable.length ? ` (${releasable.length})` : ""}
          </Button>
        </Flex>
        {activity.questions.length > 1 && (
          <Flex gap="5px" overflowX="auto" data-own-scroll sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
            {[{ guid: null }, ...activity.questions].map((q, i) => {
              const on = questionGuid === q.guid;
              return (
                <Box key={q.guid || "all"} as="button" type="button" onClick={() => setQuestionGuid(q.guid)}
                  flexShrink={0} px="10px" h="26px" borderRadius="200px" fontSize="11.5px"
                  fontWeight={on ? "600" : "400"} bg={on ? c.solidBg : c.surface} color={on ? c.solidText : c.textSub}
                  border="1px solid" borderColor={on ? c.solidBg : c.borderSoft}>
                  {q.guid ? `Q${i}` : isRace ? "This question" : "All"}
                </Box>
              );
            })}
          </Flex>
        )}
        <Flex gap="3px" p="3px" borderRadius="10px" bg={c.surfaceSubtle} border="1px solid" borderColor={c.borderSoft}>
          {[
            { id: "all", label: `All answers · ${responses.length}` },
            { id: "eyes", label: `Needs your eyes · ${needsEyes.length}` },
          ].map((t) => (
            <Box key={t.id} as="button" type="button" flex="1" onClick={() => setFilter(t.id)} py="5px" borderRadius="8px"
              fontSize="12px" fontWeight={filter === t.id ? "600" : "400"} bg={filter === t.id ? c.surface : "transparent"}
              color={filter === t.id ? c.text : c.textSub}>
              {t.label}
            </Box>
          ))}
        </Flex>
      </Flex>

      <Flex direction="column" gap="8px" px="16px" pb="16px" flex="1 1 auto" minH={0} overflowY="auto" data-own-scroll>
        {list.length === 0 && (
          <Text fontSize="12.5px" color={c.textMuted} textAlign="center" py="18px">
            {filter === "eyes" ? "Nothing needs a second look." : "Answers appear here as they come in."}
          </Text>
        )}
        {list.map((r) => (
          <AnswerCard key={r.guid} r={r} total={byQuestion[r.question_guid]?.total_mark || 0} onReview={review} onRelease={release} />
        ))}
      </Flex>
    </Flex>
  );
};

export default LiveActivityPanel;
